import React, { FC, useMemo } from 'react';
import { Checkbox, Tooltip } from '@clearcover/atlas';
import { useFormikContext } from 'formik';
import { FormInputErrorMessage } from 'Shared/components';
import { useAuth0 } from 'Auth';
import { PartnerType } from 'Admin/Partners';
import { UserType } from 'Admin/Users/types';
import styles from './styles.module.css';

export type UserRoleType =
  | 'agent'
  | 'admin'
  | 'support_staff'
  | 'underwriter'
  | 'principal'
  | 'commissions_admin'
  | 'insurance_product';

export type SystemRoleType = {
  id: string;
  name: UserRoleType;
  assignable: boolean;
};

export type SystemRolesType = {
  systemRoles: Array<SystemRoleType>;
};

type RoleOption = {
  label: string;
  name: UserRoleType;
  assignable: boolean;
};

type UserRolesForm = Record<string, string> & {
  roles: Array<UserRoleType>;
};

const systemRoles = ['admin'];

export const roleLabelMap = {
  agent: 'Agent',
  admin: 'Admin',
  support_staff: 'Support Staff',
  underwriter: 'Clearcover Underwriting Staff',
  principal: 'Principal',
  commissions_admin: 'Commissions Admin',
  insurance_product: 'Insurance Product',
};

// defined here for testing convenience
export const getPrincipalRoleTooltip = (
  role: UserRoleType,
  disabled: boolean,
  isInternalUser: boolean,
  roleIsEditableByUser: boolean,
  currentPartner?: Partial<PartnerType>
) => {
  if (role !== 'principal' || disabled || roleIsEditableByUser) {
    return undefined;
  }

  if (isInternalUser) {
    return currentPartner?.principalUser
      ? 'Principal agent already assigned for this agency.'
      : undefined;
  }

  return 'This role is only editable by Clearcover admins.';
};

// roles are passed in as a prop to make testing more convenient
export const UserRoles: FC<{
  roles: SystemRolesType['systemRoles'];
  currentPartner?: Partial<PartnerType>;
  currentUser?: Partial<UserType>;
  disabled?: boolean;
}> = ({ roles, currentPartner, currentUser, disabled = false }) => {
  const { user } = useAuth0();
  const { values, setFieldValue, touched, setTouched, errors } =
    useFormikContext<UserRolesForm>();

  const isInternalUser = user!.isCarrierUser || user!.isSystemUser;

  const roleOptions: Array<RoleOption> = useMemo(() => {
    const order = Object.keys(roleLabelMap);
    // make a copy to modify
    const availableRoles = [...roles];

    return (
      availableRoles
        // only allow system level roles when there is no partner
        .filter((role) =>
          currentPartner ? true : systemRoles.includes(role.name)
        )
        // transform the roles into checkbox options
        .map((role) => ({
          label: roleLabelMap[role.name],
          ...role,
        }))
        .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name))
    );
  }, [currentPartner, roles]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.rolesWrapper}>
        {roleOptions.map((option) => {
          // Assignable relates to whether the role is allowed to be assigned to this user based on role_config in reznor.
          // for example, the principal role may be assigned because:
          // - the logged in user is a carrier or system admin AND
          // - there is not already a principal user on the partner

          // Revocable relates to whether the logged in user making the change is allowed to remove the role.
          // The role will not exist on the user if they do not have it.
          // This handles the case where the role is not assignable, but should still be able to be removed
          // for example, if the logged in user is a carrier or system admin and they are viewing the user with the principal role

          const currentGrant = (currentUser?.portalUserGrants || []).find(
            (grant) => grant.role.name === option.name
          );

          const roleIsEditableByUser = currentGrant
            ? currentGrant.revocable
            : option.assignable;

          const principalRoleTooltip = getPrincipalRoleTooltip(
            option.name,
            disabled,
            isInternalUser,
            roleIsEditableByUser,
            currentPartner
          );

          return (
            <div className={styles.role} key={option.name}>
              <Tooltip tooltip={principalRoleTooltip} position="top">
                <Checkbox
                  label={option.label}
                  error={touched.roles && !!errors.roles}
                  disabled={disabled || !roleIsEditableByUser}
                  name={option.name}
                  value={values.roles.some((role) => role === option.name)}
                  onChange={(checked) => {
                    setFieldValue(
                      'roles',
                      checked
                        ? [...values.roles, option.name]
                        : values.roles.filter((role) => role !== option.name)
                    );
                    setTouched({ ...touched, roles: true });
                  }}
                />
              </Tooltip>
            </div>
          );
        })}
      </div>
      {touched.roles && !!errors.roles && (
        <FormInputErrorMessage message="At least one role must be selected" />
      )}
    </div>
  );
};
